// @flow

import * as React from 'react';
import cn from 'classnames';
import Box from 'sail/Box';
import {BodyMono, CaptionAltMono} from 'sail/Text';
import Icon from 'sail/Icon';

import {TerminalPrompt} from './input';
import StripeCluiSuggestions from '../suggestions';
import {useStripeClui} from '../helpers/react/provider';
import WelcomePrompt from '../components/WelcomePrompt';

import type {SuggestionsType} from '../helpers/react/reducer';

type Props = {
  handleKeyDown: (SyntheticKeyboardEvent<HTMLInputElement>) => ?boolean,
  paneIdx: number,
  inputLineHeight: number,
  cliInput: React.Node,
  suggestions: SuggestionsType,
  handleSuggestion: (idx: number) => void,
};

const TERMINAL_PANE_TOP_PADDING = 12;

const StripeCluiTerminalPane = ({
  handleKeyDown,
  suggestions,
  handleSuggestion,
  inputLineHeight,
  cliInput,
  paneIdx,
}: Props): React.Node => {
  const {
    state: {isFocused, panes, focusedPane, locked},
    focusPane,
    removePane,
  } = useStripeClui();

  const {history, showWelcomePrompt} = panes[paneIdx];

  const cluiTerminalRef = React.useRef(null);

  React.useEffect(() => {
    if (isFocused && focusedPane === paneIdx && cluiTerminalRef.current) {
      cluiTerminalRef.current.focus();
    }
  }, [isFocused, focusedPane, paneIdx]);

  const handleFocus = () => focusPane(paneIdx);

  const paneTitle =
    history[history.length - 1]?.command
      ?.split(' ')
      .slice(0, 2)
      .join(' ') || 'stripe';

  const suggestionWrapperHeight = Math.max(
    (cluiTerminalRef?.current?.clientHeight ?? 0) -
      inputLineHeight -
      TERMINAL_PANE_TOP_PADDING,
    0,
  );

  return (
    <div
      className="StripeCluiTerminalPaneWrapper"
      onClick={handleFocus}
      role="tabpanel"
    >
      {panes.length > 1 && (
        <Box
          className="StripeCluiTerminalPaneHeader"
          flex={{alignItems: 'center', justifyContent: 'space-between'}}
          style={
            focusedPane === paneIdx
              ? {
                  background: 'var(--sail-color-gray-50)',
                }
              : {
                  background: '#E3E8EE',
                  borderRadius: '8px 8px 0px 0px',
                  color: 'var(--sail-color-gray-500)',
                }
          }
        >
          <CaptionAltMono color="inherit">{paneTitle}</CaptionAltMono>
          <button
            className="StripeCluiTerminalPaneButton"
            onClick={(e) => {
              e.stopPropagation();
              removePane(paneIdx);
            }}
            type="button"
            disabled={locked}
          >
            {!locked && <Icon icon="cancel" color="inherit" size={12} />}
          </button>
        </Box>
      )}
      <div
        className={cn('StripeCluiTerminalPane', {
          'StripeCluiTerminalPane--focused':
            isFocused && focusedPane === paneIdx,
        })}
        tabIndex={paneIdx + 1}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        aria-label="Stripe CLI"
        role="textbox"
        ref={cluiTerminalRef}
        style={
          isFocused && focusedPane === paneIdx
            ? {
                background: 'var(--sail-color-blue-800)',
              }
            : {
                background: 'var(--sail-color-blue-900)',
              }
        }
      >
        <Box
          padding={{horizontal: 24, top: TERMINAL_PANE_TOP_PADDING, bottom: 0}}
          flex={{
            direction: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
          }}
          style={{
            minHeight: '100%',
            // This prevents default behavior where the div autoscrolls
            // to the bottom when new content is added.
            overflowAnchor: 'none',
            opacity: isFocused && focusedPane === paneIdx ? '1' : '0.6',
          }}
        >
          {showWelcomePrompt && (
            <Box padding={{bottom: 24}}>
              <WelcomePrompt />
            </Box>
          )}
          {history.map((commandOutput, idx) => {
            if (commandOutput.command.includes('stripe listen')) {
              return (
                <React.Fragment key={idx}>
                  <pre style={{marginBottom: 12}}>
                    <code>
                      <BodyMono color="white" wordBreak="word">
                        {<TerminalPrompt />} {commandOutput.command} {'\n'}
                      </BodyMono>
                    </code>
                  </pre>

                  {commandOutput.output}
                </React.Fragment>
              );
            } else {
              return (
                <React.Fragment key={idx}>
                  <pre style={{marginBottom: 12}}>
                    <code>
                      {!commandOutput.isPrintOutput && (
                        <BodyMono
                          color="white"
                          wordBreak="word"
                          style={{whiteSpace: 'pre-wrap'}}
                        >
                          {<TerminalPrompt />} {commandOutput.command} {'\n'}
                        </BodyMono>
                      )}
                      <pre>
                        <code className={'CodeBlock'}>
                          {commandOutput.output}
                        </code>
                      </pre>
                    </code>
                  </pre>
                </React.Fragment>
              );
            }
          })}
          {cliInput}
          <StripeCluiSuggestions
            suggestions={suggestions}
            handleSuggestion={handleSuggestion}
            suggestionWrapperHeight={suggestionWrapperHeight}
            isPaneFocused={focusedPane === paneIdx}
          />
        </Box>
      </div>
    </div>
  );
};

export default StripeCluiTerminalPane;
